import React, { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { ArrowRight } from "lucide-react";
import "./styles/globals.css";

const OrgEqHomepage: FC = () => {
  const navigate = useNavigate();
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const rotatingWords = ["what", "why", "how", "when"];
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  useEffect(() => {
    // Set page title and meta description to OrgEQ
    document.title = "OrgEQ";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Understand the what, why, how, and when behind your team\'s sentiment. OrgEQ automatically analyzes team communications to reveal sentiment trends, improve collaboration, and build healthier organizations.');
    }
    
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);

    return () => {
      clearInterval(interval);
      // Optionally restore original title on unmount, but not necessary
    };
  }, []);

  useEffect(() => {
    // Load HubSpot forms script
    if (!document.getElementById('hubspot-forms-script')) {
      const script = document.createElement('script');
      script.id = 'hubspot-forms-script';
      script.src = '//js.hsforms.net/forms/embed/v2.js';
      script.charset = 'utf-8';
      script.type = 'text/javascript';
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    // Create HubSpot form when modal opens
    if (isDemoModalOpen && (window as any).hbspt) {
      // Small delay to ensure the container is rendered
      setTimeout(() => {
        const container = document.getElementById('hubspot-form-container');
        if (container) {
          // Clear any existing form
          container.innerHTML = '';
          // Create new form
          (window as any).hbspt.forms.create({
            portalId: "46304418",
            formId: "a9e695f4-b3b9-4996-b229-7a54b6e4de32",
            region: "na1",
            target: '#hubspot-form-container'
          });
        }
      }, 100);
    }
  }, [isDemoModalOpen]);

  const handleGetStarted = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/org-eq/login");
  };

  return (
    <div className="org-eq-dashboard min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="absolute w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pulseGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4">
                <animate
                  attributeName="stop-color"
                  values="#06b6d4; #14b8a6; #10b981; #06b6d4"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="#14b8a6">
                <animate
                  attributeName="stop-color"
                  values="#14b8a6; #10b981; #06b6d4; #14b8a6"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#10b981">
                <animate
                  attributeName="stop-color"
                  values="#10b981; #06b6d4; #14b8a6; #10b981"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <path
            d="M-200 50 L-150 50 L-100 50 L-80 30 L-60 70 L-40 50 L0 50 L50 50 L100 50 L120 30 L140 70 L160 50 L200 50 L250 50 L300 50 L320 30 L340 70 L360 50 L400 50 L450 50 L500 50 L520 30 L540 70 L560 50 L600 50 L650 50 L700 50 L720 30 L740 70 L760 50 L800 50 L850 50 L900 50 L920 30 L940 70 L960 50 L1000 50 L1050 50 L1100 50 L1120 30 L1140 70 L1160 50 L1200 50 L1250 50 L1300 50 L1320 30 L1340 70 L1360 50 L1400 50 L1450 50 L1500 50 L1520 30 L1540 70 L1560 50 L1600 50 L1650 50 L1700 50 L1720 30 L1740 70 L1760 50 L1800 50 L1850 50 L1900 50 L1920 30 L1940 70 L1960 50 L2000 50"
            stroke="url(#pulseGradient)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to="400 0"
              dur="4s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3">
            <img src="/logos/orgeq-logo.svg" alt="OrgEQ" className="h-14 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setIsDemoModalOpen(true)}>
              Book a Demo
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/org-eq/login")}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-6 py-12 lg:py-16 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center space-y-6">
            {/* Headline */}
            <div className="space-y-5">
              <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-center">
                Understand the{" "}
                <span className="inline-block min-w-[4.5ch]">
                  <span
                    key={rotatingWords[currentWordIndex]}
                    className="text-cyan-600"
                    style={{
                      animation: 'fadeInWord 0.5s ease-in-out'
                    }}
                  >
                    {rotatingWords[currentWordIndex]}
                  </span>
                </span>{" "}
                <br />
                behind your team's sentiment
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto text-center text-balance">
                OrgEQ automatically analyzes team communications to reveal sentiment trends, improve collaboration, and
                build healthier organizations.
              </p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 mx-auto rounded-xl bg-cyan-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-base text-center">Analyze Communication Channels</h3>
              <p className="text-muted-foreground text-sm text-center">
                Analyze and understand key themes across all of your team's communications
              </p>
            </div>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 mx-auto rounded-xl bg-purple-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="font-semibold text-base text-center">Track Sentiment Across Topics</h3>
              <p className="text-muted-foreground text-sm text-center">
                Monitor sentiment trends over time and identify patterns that impact team performance
              </p>
            </div>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-base text-center">Create Positive Change</h3>
              <p className="text-muted-foreground text-sm text-center">
                Get actionable insights to improve team health, collaboration, and organizational culture
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center space-y-4 mt-12">
            <div className="max-w-md mx-auto">
              <form className="flex gap-3" onSubmit={handleGetStarted}>
                <Input type="email" placeholder="Enter your work email" className="flex-1 h-11 text-base" required />
                <Button type="submit" size="lg" className="bg-cyan-600 hover:bg-cyan-700 h-11 px-6">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </div>

            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <span>Already have an account?</span>
              <Button
                variant="link"
                className="text-cyan-600 hover:text-cyan-700 p-0 h-auto font-semibold"
                onClick={() => navigate("/org-eq/login")}
              >
                Sign in →
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Book a Demo</DialogTitle>
            <DialogDescription>
              Fill out the form below and we'll get in touch to schedule your personalized demo.
            </DialogDescription>
          </DialogHeader>
          <div id="hubspot-form-container" className="mt-4"></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgEqHomepage;

